import React from 'react';
import { MEDIA_STATUS } from 'features/video/stores';
import type { MediaEventAction } from 'features/video/stores';

/**
 * On schedule change: clears mini TV run, stops videos, resets media store,
 * clears all intervals/timeouts, and resets refs. Ensures no stale state
 * from the previous schedule leaks into the new one.
 *
 * The heavy reset (SET_RESULTS, SET_STATUS, SET_VIDEO_OVERLAYS) only runs on
 * actual schedule *changes*, not on the initial mount, to avoid prematurely
 * setting HAS_ENDED and triggering the win-checker cascade.
 */
export function useScheduleChangeCleanup(params: {
  currentScheduleId: string | number | null;
  clearRun: (scheduleId: string | number) => void;
  hardStopMiniTvVideos: () => void;
  mediaDispatch: React.Dispatch<MediaEventAction>;
  setTvAccordionExpanded: (value: boolean | ((prev: boolean) => boolean)) => void;
  resetLivePin: () => void;
  onCleanup?: (prevScheduleId: string | number | null) => void;
}): {
  scheduleJustChanged: boolean;
  prevScheduleIdRef: React.MutableRefObject<string | number | null>;
} {
  const {
    currentScheduleId,
    clearRun,
    hardStopMiniTvVideos,
    mediaDispatch,
    setTvAccordionExpanded,
    resetLivePin,
    onCleanup,
  } = params;

  const prevScheduleIdRef = React.useRef(currentScheduleId);
  const scheduleJustChanged = prevScheduleIdRef.current !== currentScheduleId;
  const isInitialMountRef = React.useRef(true);

  React.useLayoutEffect(() => {
    const isInitial = isInitialMountRef.current;
    isInitialMountRef.current = false;

    const sid = currentScheduleId != null ? String(currentScheduleId) : undefined;

    if (isInitial) {
      // First mount: only set the active schedule, don't blast ENDED/empty
      if (sid != null) {
        mediaDispatch({ type: 'SET_ACTIVE_SCHEDULE', scheduleId: sid });
      }
      prevScheduleIdRef.current = currentScheduleId;
      return;
    }

    const prevId = prevScheduleIdRef.current;
    if (prevId != null && String(prevId) !== String(currentScheduleId)) {
      clearRun(prevId);
      setTvAccordionExpanded(false);
      onCleanup?.(prevId);
    }
    prevScheduleIdRef.current = currentScheduleId;
    hardStopMiniTvVideos();
    resetLivePin();
    if (sid != null) {
      mediaDispatch({ type: 'SET_ACTIVE_SCHEDULE', scheduleId: sid });
    }
    mediaDispatch({ type: 'SET_RESULTS', payload: {}, scheduleId: sid });
    mediaDispatch({ type: 'SET_STATUS', payload: MEDIA_STATUS.ENDED, scheduleId: sid });
    mediaDispatch({ type: 'SET_VIDEO_OVERLAYS', payload: {}, scheduleId: sid });
  }, [currentScheduleId, clearRun, hardStopMiniTvVideos, mediaDispatch, setTvAccordionExpanded, resetLivePin, onCleanup]);

  return { scheduleJustChanged, prevScheduleIdRef };
}
